import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HomeRounded as HomeIcon,
  TodayRounded as TodayIcon,
  HourglassEmptyRounded as WaitingIcon,
  NotificationsRounded as UpdatesIcon,
} from '@mui/icons-material';
import {
  getOverviewMobileNavActiveItem,
  getOverviewMobileNavMode,
  isOverviewMobileNavPath,
  OVERVIEW_MOBILE_NAV_PATHS,
} from './overviewMobileNavPaths';
import { isAllowedPathWhileEmailUnverified } from '../../email-verification/appEmailVerificationRestriction';
import './OverviewMobileBottomNav.css';
import { useAppEmailVerificationRestricted } from '../../email-verification/EmailVerificationRestrictionContext';
import { APP_EMAIL_VERIFICATION_LOCK_TITLE } from '../../email-verification/appEmailVerificationRestriction';
import '../../email-verification/appEmailVerificationLock.css';

const OverviewMobileBottomNav = ({
  todayCount = 0,
  pendingCount = 0,
  updatesCount = 0,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const navigationLocked = useAppEmailVerificationRestricted();

  if (!isOverviewMobileNavPath(pathname)) {
    return null;
  }

  const mode = getOverviewMobileNavMode(pathname);
  const activeItem = getOverviewMobileNavActiveItem(pathname);
  const paths = OVERVIEW_MOBILE_NAV_PATHS[mode];

  const items = [
    {
      id: 'home',
      label: 'Home',
      path: paths.home,
      icon: HomeIcon,
      count: 0,
    },
    {
      id: 'today',
      label: 'Today',
      path: paths.today,
      icon: TodayIcon,
      count: todayCount,
    },
    {
      id: 'pending',
      label: mode === 'appointments' ? 'Waiting' : 'Pending',
      path: paths.pending,
      icon: WaitingIcon,
      count: pendingCount,
    },
    {
      id: 'updates',
      label: 'Updates',
      path: paths.updates,
      icon: UpdatesIcon,
      count: updatesCount,
    },
  ];

  const handleNavigate = (path) => {
    if (navigationLocked && !isAllowedPathWhileEmailUnverified(path)) return;
    if (pathname === path) return;
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nav = (
    <nav
      className={`overview-mobile-bottom-nav${navigationLocked ? ' overview-mobile-bottom-nav--locked' : ''}`}
      aria-label="Overview sections"
    >
      {items.map(({ id, label, path, icon: Icon, count }) => {
        const isActive = activeItem === id;
        const isAllowed = !navigationLocked || isAllowedPathWhileEmailUnverified(path);

        return (
          <button
            key={id}
            type="button"
            className={`overview-mobile-bottom-nav__item${isActive ? ' overview-mobile-bottom-nav__item--active' : ''}${isAllowed ? ' overview-mobile-bottom-nav__item--allowed' : ''}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => handleNavigate(path)}
            disabled={navigationLocked && !isAllowed}
            title={navigationLocked && !isAllowed ? APP_EMAIL_VERIFICATION_LOCK_TITLE : undefined}
          >
            <span className="overview-mobile-bottom-nav__icon-wrap">
              <Icon className="overview-mobile-bottom-nav__icon" />
              {count > 0 ? (
                <span className="overview-mobile-bottom-nav__badge" aria-label={`${count} items`}>
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </span>
            <span className="overview-mobile-bottom-nav__label">{label}</span>
          </button>
        );
      })}
    </nav>
  );

  return createPortal(nav, document.body);
};

export default OverviewMobileBottomNav;
